/**
 * Feature Flags Configuration
 * 
 * Controls visibility of experimental or deprecated features.
 * Use this to soft-deprecate features before permanent removal.
 */

export interface FeatureFlags {
  // Graph Explorer - Soft deprecated 2026-01-02
  // Reason: Focus on autonomous decisions, not manual hunting
  // Review: 2026-07-01 (180 days)
  // See: ADR-001-Remove-Graph-Explorer.md
  enableGraphExplorer: boolean;
  
  // Individual graph routes
  enableGraphCanvas: boolean;        // /graph
  enableGraphExplorerRoute: boolean; // /graph-explorer
  enableQueryBuilder: boolean;       // /graph-query
  enableEntityDetailRoute: boolean;  // /entity/:entityId

  // APB-1600: Real LLM-backed BRAIN Assistant. When false, the
  // component renders in its original rule-based fallback mode
  // (keyword matching against exec metrics). When true, requests
  // route to /api/assistant/chat. Per-tenant rollout is planned for
  // Phase 5 via BRAIN's feature_visibility_routes.
  enableBrainAssistant: boolean;
}

// Default flags - Set to false to hide features
export const featureFlags: FeatureFlags = {
  // SOFT DEPRECATED: 2026-01-02
  // Hidden for 180 days to evaluate if anyone requests them
  // If no requests by 2026-07-01, permanently remove
  enableGraphExplorer: false,
  enableGraphCanvas: false,
  enableGraphExplorerRoute: false,
  enableQueryBuilder: false,
  enableEntityDetailRoute: false,

  // APB-1600: default off in v1. Flip on per-tenant via localStorage
  // override (ENABLE_BRAIN_ASSISTANT=true) or once Phase 5 wires up
  // BRAIN-side feature_visibility_routes.
  enableBrainAssistant: false,
};

// Admin override - Set to true to enable for testing/demo
// Usage: localStorage.setItem('ENABLE_GRAPH_EXPLORER', 'true')
export const getFeatureFlag = (flag: keyof FeatureFlags): boolean => {
  // Check localStorage override (for admins/testing)
  const overrideKey = `ENABLE_${flag.replace(/^enable/, '').toUpperCase()}`;
  const override = localStorage.getItem(overrideKey);
  if (override === 'true') return true;
  if (override === 'false') return false;
  
  // Return default flag value
  return featureFlags[flag];
};

// Helper: Check if Graph Explorer is enabled
export const isGraphExplorerEnabled = (): boolean => {
  return getFeatureFlag('enableGraphExplorer');
};

// Deprecation notice for hidden features
export const GRAPH_EXPLORER_DEPRECATION = {
  hiddenDate: '2026-01-02',
  reviewDate: '2026-07-01',
  reason: 'Focus on autonomous decision vision',
  alternative: 'Use Threat Decision Center for alert investigation',
  adr: 'ADR-001-Remove-Graph-Explorer.md',
  
  // Track if anyone asks about it
  requestCount: 0,
  lastRequested: null as string | null,
  
  // Log a request
  logRequest: (source: string) => {
    GRAPH_EXPLORER_DEPRECATION.requestCount++;
    GRAPH_EXPLORER_DEPRECATION.lastRequested = new Date().toISOString();
    console.warn(`[DEPRECATION] Graph Explorer requested from: ${source}`);
    console.warn(`[DEPRECATION] Total requests: ${GRAPH_EXPLORER_DEPRECATION.requestCount}`);
    console.warn(`[DEPRECATION] To enable: localStorage.setItem('ENABLE_GRAPH_EXPLORER', 'true')`);
  }
};
